import "./App.css";
import { lazy, Suspense, useRef, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./providers/ThemeProvider";
import { useAuthContext } from "./hooks/useAuthContext";
import Loading from "./components/Loading";
import { Toaster } from "@/shadcn/components/ui/toaster";
import { UserDocProvider } from "./contexts/UserDocContext";
import useMediaQuery from "./hooks/useMediaQuery";

// Componentes que sempre são necessários (não lazy)
import Sidebar from "./components/Sidebar";
import Topbar from "./components/Topbar";
import TopbarMobile from "./components/TopbarMobile";
import BottomBar from "./components/BottomBar";

// Lazy loading das páginas
const Login = lazy(() => import("./pages/Login/Login"));
const Profile = lazy(() => import("./pages/Profile/Profile"));
const PasswordRecovery = lazy(() => import("./pages/Recover/Recover"));
const Training = lazy(() => import("./pages/Training/Training"));
const Dashboard = lazy(() => import("./pages/Dashboard/Dashboard"));
const Admin = lazy(() => import("./pages/AdmPanel/Admin"));
const Material = lazy(() => import("./pages/Material/Material"));
const Progress = lazy(() => import("./pages/Progress/Progress"));

function AppRoutes() {
  const { user, authIsReady } = useAuthContext();
  const [rerender, setRerender] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isWatching, setIsWatching] = useState(false);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const scrollToTop = useRef(null);
  const [activeRoute, setActiveRoute] = useState(0);

  if (!authIsReady) {
    console.log("Not auth is ready");
    return <Loading />;
  }

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <div
        ref={scrollToTop}
        className="App flex flex-col sm:flex-row bg-background "
      >
        <Toaster />
        <BrowserRouter>
          {user ? (
            <UserDocProvider user={user}>
              {isMobile ? (
                <TopbarMobile setRerender={setRerender} />
              ) : (
                <Topbar setRerender={setRerender} />
              )}
              {isMobile ? null : (
                <div
                  className={`${
                    sidebarExpanded ? "w-[284px]" : "w-[90px]"
                  } w-[286px] h-[calc(100vh_-_120px)] fixed top-24 left-5 bottom-5 overflow-y-hidden border rounded-md`}
                >
                  <Sidebar
                    rerender={rerender}
                    setRerender={setRerender}
                    sidebarExpanded={sidebarExpanded}
                    setSidebarExpanded={setSidebarExpanded}
                    isWatching={isWatching}
                    setIsWatching={setIsWatching}
                    activeRoute={activeRoute}
                    setActiveRoute={setActiveRoute}
                  />
                </div>
              )}
              <div
                className={`${
                  sidebarExpanded && !isWatching
                    ? "sm:w-[calc(100%_-_300px)] sm:ml-[310px]"
                    : "sm:w-[calc(100%_-_40px)] sm:ml-[112px]"
                } mt-[80px] px-2.5 sm:px-5 sm:mt-[98px]`}
              >
                <Suspense fallback={<Loading />}>
                  <Routes>
                  <Route
                    exact
                    path="/"
                    element={
                      <Dashboard
                        sidebarExpanded={sidebarExpanded}
                        setSidebarExpanded={setSidebarExpanded}
                        isWatching={isWatching}
                        setIsWatching={setIsWatching}
                        activeRoute={activeRoute}
                        setActiveRoute={setActiveRoute}
                      />
                    }
                  />
                  <Route
                    path="/aulas"
                    element={
                      <Training
                        scrollToTop={scrollToTop}
                        sidebarExpanded={sidebarExpanded}
                        setSidebarExpanded={setSidebarExpanded}
                        isWatching={isWatching}
                        setIsWatching={setIsWatching}
                      />
                    }
                  />
                  <Route
                    path="/conta"
                    element={
                      <Profile rerender={rerender} setRerender={setRerender} />
                    }
                  />
                  <Route path="/material" element={<Material />} />
                  <Route path="/progresso" element={<Progress />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route
                    path="*"
                    element={
                      <Dashboard
                        sidebarExpanded={sidebarExpanded}
                        setSidebarExpanded={setSidebarExpanded}
                        isWatching={isWatching}
                        setIsWatching={setIsWatching}
                      />
                    }
                  />
                  </Routes>
                </Suspense>
              </div>
              {isMobile ? (
                <div className="h-16 fixed bottom-0 left-0">
                  <BottomBar />
                </div>
              ) : null}
            </UserDocProvider>
          ) : (
            <Suspense fallback={<Loading />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/password/recovery" element={<PasswordRecovery />} />
              <Route path="*" element={<Login />} />
              </Routes>
            </Suspense>
          )}
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default AppRoutes;
